import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface AdminUser {
  id: string;
  username: string;
}

interface AdminAuthState {
  user: AdminUser | null;
  token: string | null;
  isLoading: boolean;
}

export const useAdminAuth = () => {
  const [authState, setAuthState] = useState<AdminAuthState>({
    user: null,
    token: null,
    isLoading: true
  });

  useEffect(() => {
    // Check for existing session in localStorage
    const token = localStorage.getItem('admin_token');
    const user = localStorage.getItem('admin_user');
    
    if (token && user) {
      setAuthState({
        user: JSON.parse(user),
        token,
        isLoading: false
      });
    } else {
      setAuthState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('admin-auth', {
        body: { username, password },
        method: 'POST'
      });

      if (error) throw error;

      if (data.success) {
        const { token, user } = data;
        localStorage.setItem('admin_token', token);
        localStorage.setItem('admin_user', JSON.stringify(user));
        
        setAuthState({
          user,
          token,
          isLoading: false
        });
        
        return { success: true };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Login failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    setAuthState({
      user: null,
      token: null,
      isLoading: false
    });
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    if (!authState.token) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      const { data, error } = await supabase.functions.invoke('admin-auth?action=change-password', {
        body: { 
          token: authState.token, 
          username: authState.user?.username,
          currentPassword, 
          newPassword 
        }
      });

      if (error) throw error;

      return { success: data.success, error: data.error };
    } catch (error) {
      console.error('Password change error:', error);
      return { success: false, error: 'Password change failed' };
    }
  };

  return {
    ...authState,
    login,
    logout,
    changePassword
  };
};